import { useEffect, useRef, useState } from "react";
import type { Identifier } from "ra-core";

import { ChoiceChips, type ChoiceChipOption } from "../design/ChoiceChips";

export interface ProfileChipGroupOption {
  id: Identifier;
  label: string;
  /** Shown with the hint 'archivada' / 'archivado'. */
  archived?: boolean;
}

export interface ProfileChipGroupProps {
  /** Accessible name for the chip group, and the visible field label above it. */
  label: string;
  options: ProfileChipGroupOption[];
  /** Current server value; single mode holds zero or one id. */
  value: Identifier[];
  mode: "single" | "multi";
  /** Persists `nextIds` (the array `ChoiceChips` already computed); rejects to trigger a revert. */
  onCommit: (nextIds: Identifier[]) => Promise<void>;
  max?: number;
  maxHint?: string;
  readOnly: boolean;
  /** Read-only mode only: shown when `value` is empty. */
  emptyReadOnlyLabel: string;
  className?: string;
}

const ARCHIVED_HINT = "archivada";

function toChipOptions(
  options: ProfileChipGroupOption[],
  pendingId: string | null,
): ChoiceChipOption[] {
  return options.map((option) => ({
    id: String(option.id),
    label: option.label,
    hint: option.archived ? ARCHIVED_HINT : undefined,
    pending: String(option.id) === pendingId,
  }));
}

/**
 * Almikia Epic EF (TASK-119), DATA CONTRACT EF (6): one profile dimension
 * rendered as a `ChoiceChips` group with autosave -- a tap applies
 * immediately (optimistic), persists through `onCommit`, and reverts to the
 * last server value if the save fails. Read-only mode renders only the
 * selected values as non-interactive chips (never the whole catalogue), with
 * `emptyReadOnlyLabel` ("Sin dato") when there is nothing selected.
 */
export function ProfileChipGroup({
  label,
  options,
  value,
  mode,
  onCommit,
  max,
  maxHint,
  readOnly,
  emptyReadOnlyLabel,
  className,
}: ProfileChipGroupProps) {
  const [localValue, setLocalValue] = useState<Identifier[]>(value);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const isSavingRef = useRef(false);

  // Reflects a server-driven change (reload, another tab) into the chips --
  // but never while a save from THIS group is still in flight, which would
  // otherwise stomp the optimistic value with the not-yet-refetched one.
  useEffect(() => {
    if (!isSavingRef.current) {
      setLocalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  if (readOnly) {
    const selectedOptions = options.filter((option) =>
      value.some((id) => String(id) === String(option.id)),
    );
    return (
      <div className={className}>
        <span className="text-sm font-medium">{label}</span>
        {selectedOptions.length > 0 ? (
          <ChoiceChips
            label={label}
            options={toChipOptions(selectedOptions, null)}
            value={selectedOptions.map((option) => String(option.id))}
            mode={mode}
            readOnly
            className="mt-1"
          />
        ) : (
          <p className="mt-1 text-sm text-muted-foreground">
            {emptyReadOnlyLabel}
          </p>
        )}
      </div>
    );
  }

  const handleChange = async (nextIds: string[]) => {
    const previous = localValue;
    // The tapped chip is either newly added or just removed -- `ChoiceChips`
    // already computed `nextIds`, this only identifies which one to mark
    // `pending` while the save is in flight.
    const addedId = nextIds.find(
      (id) => !previous.some((current) => String(current) === id),
    );
    const removedId = previous.find(
      (current) => !nextIds.includes(String(current)),
    );
    const tappedId = addedId ?? (removedId != null ? String(removedId) : null);
    const nextValue = nextIds
      .map((id) => options.find((option) => String(option.id) === id)?.id)
      .filter((id): id is Identifier => id != null);

    setLocalValue(nextValue);
    setPendingId(tappedId ?? null);
    isSavingRef.current = true;
    try {
      await onCommit(nextValue);
    } catch {
      setLocalValue(previous);
    } finally {
      setPendingId(null);
      isSavingRef.current = false;
    }
  };

  return (
    <div className={className}>
      <span className="text-sm font-medium">{label}</span>
      <ChoiceChips
        label={label}
        options={toChipOptions(options, pendingId)}
        value={localValue.map((id) => String(id))}
        mode={mode}
        onChange={handleChange}
        max={max}
        maxHint={maxHint}
        className="mt-1"
      />
    </div>
  );
}
